import React from 'react'
import { Link, Navigate, Outlet, useLocation } from 'react-router-dom'
import { Alert, Button, Spin, Tooltip } from 'antd'
import { CompressOutlined, ExpandOutlined } from '@ant-design/icons'
import classNames from 'classnames'
import { ERoles } from 'models/auth'
import { appActions, appSelectors } from 'store/app'
import { useAppDispatch, useAppSelector } from 'shared/hooks/useReduxHooks'
import { useIsCompactMenu } from 'shared/hooks/useIsCompactMenu'
import { useBrowserNotifications } from 'shared/hooks/useBrowserNotifications'
import { useSellerChatRealtime } from 'shared/hooks/useSellerChatRealtime'
import { useSupportRealtime } from 'shared/hooks/useSupportRealtime'
import { useWideLayout } from 'shared/hooks/useWideLayout'
import { useSellerAccess } from 'shared/hooks/useSellerAccess'
import { useCabinetActivity } from 'shared/hooks/useCabinetActivity'
import { useStoreHiddenSync } from 'shared/hooks/useStoreHiddenSync'
import { sellerApi } from 'api/seller'
import { sellerEmployeesApi } from 'api/sellerEmployees'
import { sellerNoticesApi } from 'api/sellerNotices'
import { ICommissionDiscount } from 'models/seller'
import { ISellerNotice } from 'models/sellerNotices'
import { FRESH_LOGIN_STORAGE_KEY } from 'shared/constants/storage'
import { adminRoutes } from 'shared/constants/routes'
import { MANAGER_PHONE, MANAGER_PHONE_HREF } from 'shared/constants/app'
import logo from 'assets/icons/logo.svg'
import { CommissionDiscountModal } from 'components/CommissionDiscountModal/CommissionDiscountModal'
import { SellerNoticeModal } from 'components/SellerNoticeModal/SellerNoticeModal'
import { ScreenLock } from 'components/ScreenLock/ScreenLock'
import { SuperAdminMenu } from '../SuperAdminMenu/SuperAdminMenu'
import { AdminMenu } from '../AdminMenu/AdminMenu'
import { SellerTabBar } from '../MobileTabBar/SellerTabBar'
import { SuperAdminTabBar } from '../MobileTabBar/SuperAdminTabBar'
import styles from './AppWrapper.module.scss'

const CHATS_PATH = `${adminRoutes.PROFILE}/${adminRoutes.PROFILE_CHATS}`

export const AppWrapper: React.FC = () => {
  const role = useAppSelector(appSelectors.selectRole)
  const isSuperAdmin = role === ERoles.ADMIN
  const location = useLocation()
  const isCompactMenu = useIsCompactMenu()
  const { isWide, canToggle: canToggleLayout, toggle: toggleLayout } = useWideLayout()
  const sellerId = useAppSelector(appSelectors.selectSellerId)
  const employeeId = useAppSelector(appSelectors.selectEmployeeId)
  const isStoreHidden = useAppSelector(appSelectors.selectIsStoreHidden)
  const isHiddenBySeller = useAppSelector(appSelectors.selectIsHiddenBySeller)
  const dispatch = useAppDispatch()
  const syncStoreHidden = useStoreHiddenSync()
  const { isReady: isAccessReady, canOpenPath, homePath } = useSellerAccess()
  const [isAccessError, setIsAccessError] = React.useState(false)
  const [accessReloadKey, setAccessReloadKey] = React.useState(0)
  // Активная акция «сниженная комиссия» — показываем продавцу модалку при каждом входе
  const [commissionDiscount, setCommissionDiscount] = React.useState<ICommissionDiscount | null>(null)
  // Уведомления из «Действия» → «Уведомление селлерам»: по одному при каждой загрузке кабинета,
  // пока продавец не отметит «Не показывать снова»
  const [sellerNotices, setSellerNotices] = React.useState<ISellerNotice[]>([])

  // Права сотрудника не лежат в токене — берём их с сервера и перечитываем при возврате
  // на вкладку, чтобы правки владельца доходили без перезахода
  React.useEffect(() => {
    if (role !== ERoles.USER || !employeeId) return

    const loadEmployee = () => {
      sellerEmployeesApi
        .getMe()
        .then(({ data }) => {
          setIsAccessError(false)
          if (data.role === 'EMPLOYEE') dispatch(appActions.setEmployee(data.employee))
        })
        .catch(() => setIsAccessError(true))
    }
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') loadEmployee()
    }

    loadEmployee()
    document.addEventListener('visibilitychange', onVisibilityChange)
    return () => document.removeEventListener('visibilitychange', onVisibilityChange)
  }, [role, employeeId, accessReloadKey])

  // Магазин отключают и включают без участия продавца, поэтому признак перечитываем
  // при возврате на вкладку
  React.useEffect(() => {
    if (role !== ERoles.USER || !sellerId) return

    const loadStoreHidden = () => {
      sellerApi
        .getStockConfirmationStatus()
        .then(({ data }) => syncStoreHidden(data))
        .catch(() => undefined)
    }
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') loadStoreHidden()
    }

    loadStoreHidden()
    document.addEventListener('visibilitychange', onVisibilityChange)
    return () => document.removeEventListener('visibilitychange', onVisibilityChange)
  }, [role, sellerId, syncStoreHidden])

  React.useEffect(() => {
    // Скрытие уведомления общее на магазин: сотрудник не должен прятать его от владельца
    if (role !== ERoles.USER || !sellerId || employeeId) {
      setSellerNotices([])
      return
    }

    sellerNoticesApi
      .getForSeller()
      .then(({ data }) => setSellerNotices(data))
      .catch(() => undefined)

    // Флаг ставится только при входе — на refresh его уже нет, поэтому модалка не всплывает повторно
    const isFreshLogin = sessionStorage.getItem(FRESH_LOGIN_STORAGE_KEY) === '1'
    sessionStorage.removeItem(FRESH_LOGIN_STORAGE_KEY)

    sellerApi
      .getSeller(sellerId)
      .then(({ data }) => {
        const discount = data.commission_discount
        const isActive =
          Boolean(discount?.is_active) &&
          discount?.rate != null &&
          (!discount.end_date || Date.now() < new Date(discount.end_date).getTime())
        setCommissionDiscount(isFreshLogin && isActive ? discount ?? null : null)
      })
      .catch(() => undefined)
  }, [role, sellerId, employeeId])

  const currentSellerNotice = sellerNotices[0] ?? null

  const handleSellerNoticeClose = async (hide: boolean) => {
    if (!currentSellerNotice) return
    if (hide) {
      await sellerNoticesApi.hide(currentSellerNotice._id)
    }
    setSellerNotices((prev) => prev.filter((notice) => notice._id !== currentSellerNotice._id))
  }

  // Админу обращения приходят на любой странице, поэтому разрешение спрашиваем сразу,
  // а продавца не отвлекаем, пока он не открыл сообщения
  useBrowserNotifications(
    Boolean(role),
    isSuperAdmin || location.pathname.startsWith(CHATS_PATH)
  )
  useSupportRealtime(isSuperAdmin)
  useSellerChatRealtime(role === ERoles.USER)
  useCabinetActivity(role === ERoles.USER && Boolean(sellerId))

  const renderContent = () => {
    if (!isAccessReady) {
      return (
        <div className={styles.accessLoader}>
          {isAccessError ? (
            <>
              <span>Не удалось загрузить права доступа</span>
              <Button onClick={() => setAccessReloadKey((key) => key + 1)}>
                Повторить
              </Button>
            </>
          ) : (
            <Spin size='large' />
          )}
        </div>
      )
    }
    if (!canOpenPath(location.pathname)) return <Navigate to={homePath} replace />
    return <Outlet />
  }

  return (
    <div className={styles.container}>
      <div className={classNames(styles.wrapper, { [styles.wrapperWide]: isWide })}>
        <header className={styles.header}>
          <Link to='/'>
            <img src={logo} alt='Логотип компании' className={styles.logo} />
          </Link>
          {role !== ERoles.ADMIN && (
            <div className={styles.support}>
              <span>Связаться с менеджером</span>
              <a href={MANAGER_PHONE_HREF} className={styles.supportPhone}>{MANAGER_PHONE}</a>
            </div>
          )}
          <div className={styles.headerEnd}>
            {role === ERoles.ADMIN ? <SuperAdminMenu /> : <AdminMenu />}
            {canToggleLayout && (
              <Tooltip title={isWide ? 'Узкая раскладка' : 'Широкая раскладка'}>
                <Button
                  type='text'
                  icon={isWide ? <CompressOutlined /> : <ExpandOutlined />}
                  onClick={toggleLayout}
                  aria-label={isWide ? 'Узкая раскладка' : 'Широкая раскладка'}
                />
              </Tooltip>
            )}
          </div>
        </header>
        {role === ERoles.USER && isStoreHidden && (
          <Alert
            className={styles.storeHidden}
            type='warning'
            showIcon
            message={
              <>
                Ваш магазин временно отключен. Товары скрыты с витрины.
                {!isHiddenBySeller && (
                  <>
                    {' '}Обратитесь в{' '}
                    <Link to={CHATS_PATH} state={{ openSupport: true }}>
                      тех. поддержку
                    </Link>
                    .
                  </>
                )}
              </>
            }
          />
        )}
        {renderContent()}
      </div>
      {isSuperAdmin ? <SuperAdminTabBar /> : <SellerTabBar />}
      {/* Блокировка экрана нужна только супер-админу на компьютере: админку открывают на POS-терминале */}
      {role === ERoles.ADMIN && !isCompactMenu && <ScreenLock />}
      <CommissionDiscountModal
        discount={commissionDiscount}
        onClose={() => setCommissionDiscount(null)}
      />
      <SellerNoticeModal
        notice={commissionDiscount ? null : currentSellerNotice}
        onClose={handleSellerNoticeClose}
      />
    </div>
  )
}